import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const formatCurrency = (amount) =>
  `${(Number(amount) || 0).toLocaleString("vi-VN")}đ`;

const buildAdminHeaders = (withJson = false) => {
  const token = localStorage.getItem("userAccessToken");
  const adminPassword =
    process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;
  return {
    ...(withJson ? { "Content-Type": "application/json" } : {}),
    ...(adminPassword ? { "X-Admin-Password": adminPassword } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const STATUS_LABELS = {
  pending: "Chờ duyệt",
  approved: "Đã duyệt",
  completed: "Hoàn tất",
  rejected: "Từ chối",
};

const AdminWithdrawalsScreen = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/withdrawals?status=${statusFilter}`,
        { headers: buildAdminHeaders() }
      );
      const result = await response.json();
      if (result.success) {
        setWithdrawals(result.withdrawals);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Lỗi khi tải danh sách rút tiền:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [statusFilter]);

  const handleOpenProcessModal = (w) => {
    setSelectedWithdrawal(w);
    setAdminNote(w.adminNote || "");
    setShowProcessModal(true);
  };

  const handleProcess = async (action) => {
    if (!selectedWithdrawal) return;
    if (action === "rejected" && !adminNote.trim()) {
      alert("Vui lòng nhập lý do từ chối");
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/withdrawals/${selectedWithdrawal.id}/process`,
        {
          method: "POST",
          headers: buildAdminHeaders(true),
          body: JSON.stringify({ action, adminNote }),
        }
      );
      const result = await response.json();

      if (result.success) {
        alert("Xử lý yêu cầu thành công");
        setShowProcessModal(false);
        fetchWithdrawals();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      alert(`Lỗi: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Quản lý Rút Tiền Affiliate</h2>
          <p className="text-sm text-gray-500 mt-1">Duyệt và quản lý các yêu cầu rút tiền hoa hồng.</p>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-4 py-2 bg-white"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="pending">Chờ duyệt</option>
            <option value="approved">Đã duyệt (Chờ chuyển khoản)</option>
            <option value="completed">Hoàn tất</option>
            <option value="rejected">Từ chối</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Đang tải...</div>
        ) : withdrawals.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Không có yêu cầu rút tiền nào.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mã / Ngày</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số tiền</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thông tin Ngân hàng</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Trạng thái</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hành động</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {withdrawals.map((w) => (
                <tr key={w.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]" title={w.id}>{w.id}</div>
                    <div className="text-xs text-gray-500">
                      {w.createdAt ? new Date(w.createdAt._seconds ? w.createdAt._seconds * 1000 : w.createdAt).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" }) : "N/A"}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{w.userEmail || "N/A"}</div>
                    <div className="text-xs text-gray-500 break-all">{w.userId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">{formatCurrency(w.amount)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 font-medium">{w.accountHolder}</div>
                    <div className="text-sm text-gray-500">{w.accountNumber}</div>
                    <div className="text-xs text-gray-500">{w.bankName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[w.status] || "bg-gray-100 text-gray-800"}`}>
                      {STATUS_LABELS[w.status] || w.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenProcessModal(w)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Xử lý / Chi tiết
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showProcessModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Xử lý Yêu cầu Rút tiền</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm"><span className="text-gray-500 w-24 inline-block">Mã yêu cầu:</span> <strong>{selectedWithdrawal.id}</strong></p>
                <p className="text-sm"><span className="text-gray-500 w-24 inline-block">Số tiền:</span> <strong className="text-red-600 text-lg">{formatCurrency(selectedWithdrawal.amount)}</strong></p>
                <p className="text-sm"><span className="text-gray-500 w-24 inline-block">Trạng thái:</span> <span className={`px-2 py-0.5 text-xs font-semibold rounded ${STATUS_COLORS[selectedWithdrawal.status]}`}>{STATUS_LABELS[selectedWithdrawal.status]}</span></p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Thông tin chuyển khoản:</h4>
                <div className="border border-gray-200 rounded p-3 text-sm">
                  <p>Ngân hàng: <strong>{selectedWithdrawal.bankFullName || selectedWithdrawal.bankName}</strong></p>
                  <p>Số tài khoản: <strong>{selectedWithdrawal.accountNumber}</strong></p>
                  <p>Chủ tài khoản: <strong>{selectedWithdrawal.accountHolder}</strong></p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú của Admin (User có thể thấy)</label>
                <textarea
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2 border"
                  rows="3"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="VD: Đã chuyển khoản..."
                ></textarea>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap gap-2 justify-end bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowProcessModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Đóng
              </button>
              
              {selectedWithdrawal.status === "pending" && (
                <>
                  <button
                    onClick={() => handleProcess("rejected")}
                    disabled={processing}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    Từ chối
                  </button>
                  <button
                    onClick={() => handleProcess("approved")}
                    disabled={processing}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    Duyệt Yêu Cầu
                  </button>
                </>
              )}
              
              {selectedWithdrawal.status === "approved" && (
                <button
                  onClick={() => handleProcess("completed")}
                  disabled={processing}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  Đánh dấu Đã Chuyển Tiền
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawalsScreen;
